import type { ReactNode } from 'react';

import { Text } from '@spotify-internal/encore-web';

import {
  ToolGroupEntry,
  isProminentToolEntry,
  useToolExtensions,
} from '../../extensions/tool-extension';
import { ChatInformationBox } from '../ChatInformationBox';
import { ElapsedTimer } from '../ElapsedTimer';
import { formatDefaultToolLabel, inferToolIcon } from './defaultToolMappings';
import { sharedToolStyles } from './sharedStyles';

import styles from './DefaultToolCallGroup.module.css';

const ToolEntryLabel = ({
  entry,
  iconSize = 14,
  isStreaming,
}: {
  entry: ToolGroupEntry;
  iconSize?: number;
  isStreaming: boolean;
}) => {
  const toolExtensions = useToolExtensions();
  const ext = toolExtensions.find(e => e.predicate(entry.tool));

  const IconComponent = ext?.icon ?? inferToolIcon(entry.tool);

  let labelContent: ReactNode;
  if (ext?.renderLabel) {
    labelContent = ext.renderLabel(entry, {
      isStreaming: entry.isStreaming ?? isStreaming,
    });
  } else {
    const label = ext?.formatLabel
      ? ext.formatLabel(entry)
      : formatDefaultToolLabel(entry);
    labelContent = (
      <Text variant="bodySmall" className={styles.text}>
        {label.text}
        {label.detail && (
          <span className={styles.text}>{` ${label.detail}`}</span>
        )}
      </Text>
    );
  }

  const icon = (
    <IconComponent size={iconSize} className={sharedToolStyles.icon} />
  );
  return (
    <div className={styles.label}>
      {icon}
      {labelContent}
      {!ext?.renderLabel && !entry.result && entry.startedAt !== undefined && (
        <ElapsedTimer startedAt={entry.startedAt} />
      )}
    </div>
  );
};

export const DefaultToolCallGroup = ({
  entries,
  isStreaming,
}: {
  entries: ToolGroupEntry[];
  isStreaming: boolean;
}) => {
  const toolExtensions = useToolExtensions();

  if (entries.length === 1) {
    const entry = entries[0];
    const ext = toolExtensions.find(e => e.predicate(entry.tool));

    if (isProminentToolEntry(ext, entry) && ext?.renderTool) {
      const renderedTool = ext.renderTool(entry, {
        isStreaming: entry.isStreaming ?? isStreaming,
      });
      if (renderedTool) return <>{renderedTool}</>;
      return (
        <div className={styles.listItem}>
          <ToolEntryLabel
            entry={entry}
            iconSize={12}
            isStreaming={isStreaming}
          />
        </div>
      );
    }

    return (
      <ChatInformationBox
        title={<ToolEntryLabel entry={entry} isStreaming={isStreaming} />}
      />
    );
  }

  return (
    <div className={styles.list}>
      {entries.map((entry, i) => {
        const ext = toolExtensions.find(e => e.predicate(entry.tool));

        if (isProminentToolEntry(ext, entry) && ext?.renderTool) {
          const renderedTool = ext.renderTool(entry, {
            isStreaming: entry.isStreaming ?? isStreaming,
          });
          if (!renderedTool) {
            return (
              <div key={i} className={styles.listItem}>
                <ToolEntryLabel
                  entry={entry}
                  iconSize={12}
                  isStreaming={isStreaming}
                />
              </div>
            );
          }

          return <div key={i}>{renderedTool}</div>;
        }

        return (
          <div key={i} className={styles.listItem}>
            <ToolEntryLabel
              entry={entry}
              iconSize={12}
              isStreaming={isStreaming}
            />
          </div>
        );
      })}
    </div>
  );
};
